import React, { forwardRef } from 'react';

import Circle from '../../base/Circle';
import Svg from '../../base/Svg';
import Box from '../../Box';
import { ProgressProps } from '../types';
import { getCircularProgressSize, getProgressColorClasses } from '../utils';

type CircularProgressProps = ProgressProps;

const CircularProgress = forwardRef<HTMLDivElement, CircularProgressProps>((props, ref) => {
  const {
    className,
    color = 'primary',
    overrides,
    size: sizeProp = 'medium',
    value: valueProp,
  } = props;

  const value = valueProp ?? 0;
  const { progress, track } = getProgressColorClasses(color);
  const { size, thickness } = getCircularProgressSize(sizeProp);

  const center = size / 2;
  const radius = (size - thickness) / 2;

  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference.toFixed(3);
  const strokeDashoffset = (((100 - value) / 100) * circumference).toFixed(3);

  return (
    <Box ref={ref} aria-valuenow={value} className={className} component="span" role="progressbar">
      <Svg fill="none" height={size} viewBox={`0 0 ${size} ${size}`} width={size}>
        <Circle
          className={track}
          cx={center}
          cy={center}
          r={radius}
          stroke="currentColor"
          strokeWidth={thickness}
        />
        <Circle
          className={overrides?.progressColor ?? progress}
          cx={center}
          cy={center}
          r={radius}
          stroke="currentColor"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeWidth={thickness}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>
    </Box>
  );
});

export type { CircularProgressProps };
export default CircularProgress;
